<template>
  <!-- max-md:-mt-12 компенсирует лишний pt-12 layout-а: на мобильном хедер уже в
       потоке, поэтому дополнительный отступ под него не нужен -->
  <div
    class="w-full px-4 pt-4 max-md:px-3 max-md:pt-3 max-md:-mt-12 flex flex-col min-h-full"
    v-if="!isLoading"
  >
    <div class="w-full max-w-[971px] mx-auto md:mt-4 flex-1 flex flex-col">
      <button class="flex items-center gap-2 mb-4 max-md:mb-3 w-fit" @click="goBack">
        <Icon name="akar-icons:chevron-left" class="text-primary text-sm" />
        <span class="text-primary text-sm max-md:text-xs font-medium">Назад</span>
      </button>

      <ChannelViewHero :place="place" />

      <!-- Табы -->
      <div class="bg-white rounded-2xl p-1 mt-4 max-md:mt-3 flex gap-2 max-md:gap-1">
        <button
          v-for="(tab, index) in tabs"
          :key="tab.full"
          class="flex-1 h-9 rounded-xl flex items-center justify-center text-sm max-md:text-xs px-2 max-md:px-1 text-center whitespace-nowrap transition-colors"
          :class="activeTab === index ? 'bg-bgnew text-primary' : 'text-txprimary'"
          @click="activeTab = index"
        >
          <span class="max-md:hidden">{{ tab.full }}</span>
          <span class="md:hidden">{{ tab.short }}</span>
        </button>
      </div>

      <!-- Купить рекламу -->
      <template v-if="activeTab === 0">
        <ChannelViewFormats :place="place" class="mt-4 max-md:mt-3" />
        <ChannelViewFaq class="mt-4 max-md:mt-3" />
      </template>
      <!-- Расширенная статистика -->
      <div v-else-if="activeTab === 1" class="mt-4 max-md:mt-3 bg-white rounded-3xl max-md:rounded-2xl p-5 max-md:p-3">
        <ChannelViewStat :place="place" />
      </div>
      <!-- Другие каналы владельца: карточки лежат прямо на фоне страницы,
           без общей белой подложки — иначе сливаются в единое полотно -->
      <div v-else-if="activeTab === 2" class="mt-4 max-md:mt-3">
        <ChannelViewOtherChannels
          :userId="place?.userId"
          :placeId="place?.id"
          :accentColor="accentColor"
        />
      </div>

      <!-- Спейсер прижимает футер к низу при коротком контенте -->
      <div class="flex-1 min-h-4 max-md:min-h-3"></div>

      <!-- Футер -->
      <div class="bg-white rounded-t-[20px] px-5 pt-3 pb-4 max-md:px-3">
        <p class="text-[13px] leading-[1.3] text-txprimary/60">
          Телеграмм канал <span class="text-primary">{{ place?.name }}</span> имеет
          {{ subscribersCount.toLocaleString() }}
          {{ plural(subscribersCount, ["подписчик", "подписчика", "подписчиков"]) }}, из которых
          {{ femaleCount.toLocaleString() }}
          {{ plural(femaleCount, ["девушка", "девушки", "девушек"]) }} и
          {{ maleCount.toLocaleString() }}
          {{ plural(maleCount, ["мужчина", "мужчины", "мужчин"]) }}. Показатель просмотров в ТГ
          канале
          {{ (place?.viewsStatistic?.countLast24h || 0).toLocaleString() }}, ER составляет
          {{ place?.er || 0 }}%. На telepost можно купить рекламу в телеграм канале
          <span class="text-primary">{{ place?.name }}</span
          >!
        </p>
        <div class="border-t border-[#EDF0F6] mt-3"></div>
        <div class="mt-4 flex items-center justify-between gap-4 max-md:flex-col max-md:items-start">
          <div class="flex items-end gap-3">
            <img src="@/public/assets/img/logo.svg" alt="logo" class="w-[35px]" />
            <img src="@/public/assets/img/logoName.svg" alt="telepost" class="h-7" />
          </div>
          <div class="flex items-center gap-6 max-md:gap-3 text-sm whitespace-nowrap">
            <span class="text-txprimary">Поддержка</span>
            <NuxtLink
              to="https://t.me/suptelepost"
              target="_blank"
              rel="nofollow noindex noopener noreferrer"
              class="text-primary"
            >
              @suptelepost
            </NuxtLink>
            <NuxtLink to="/documents" target="_blank" class="text-primary">
              Юридические документы
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="w-full p-4 mt-8 pb-0" v-else>
    <div class="flex justify-center items-center h-full">
      <UILoader />
    </div>
  </div>
</template>

<script setup lang="ts">
  import PlaceService from "~/services/PlaceService";
  import type { FullPlace } from "~/interfaces/publisher";
  import { usePlaceModuleStore } from "~/store/PlaceModule";

  const router = useRouter();
  const route = useRoute();
  const id = computed((): string => (route.params.id as string) || (route.query.id as string));
  // Забираем данные из middleware (payload) сразу в setup, чтобы дочерние компоненты получили place при mount
  const nuxtApp = useNuxtApp();
  const place = ref<FullPlace | null>((nuxtApp.payload.channelData as FullPlace) || null);
  if (nuxtApp.payload.channelData) {
    delete nuxtApp.payload.channelData;
  }
  const isLoading = ref<boolean>(!place.value);

  // На мобильном показываем короткие подписи, чтобы табы влезали в одну строку.
  const tabs = [
    { full: "Купить рекламу", short: "Купить" },
    { full: "Расширенная статистика", short: "Статистика" },
    { full: "Другие каналы владельца", short: "Другие каналы" },
  ];
  const activeTab = ref<number>(0);

  // Акцентный цвет канала — им красятся кнопки в карточках других каналов.
  const placeStore = usePlaceModuleStore();
  const { colorOptions } = storeToRefs(placeStore);
  const accentColor = computed(() =>
    place.value?.buttonsColor ? (colorOptions.value[place.value.buttonsColor] ?? "#3176FF") : "#3176FF"
  );

  // Данные для SEO-текста в футере
  const subscribersCount = computed(() => place.value?.subscribersStatistic?.count || 0);
  const femaleCount = computed(() =>
    Math.round(
      (subscribersCount.value * (place.value?.subscribersStatistic?.femalePercent || 0)) / 100
    )
  );
  const maleCount = computed(() => Math.max(subscribersCount.value - femaleCount.value, 0));

  // Склонение существительного по числу: [1, 2-4, 5-20]
  const plural = (n: number, forms: [string, string, string]) => {
    const n10 = n % 10;
    const n100 = n % 100;
    if (n10 === 1 && n100 !== 11) return forms[0];
    if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 >= 20)) return forms[1];
    return forms[2];
  };

  // === SEO ===
  // Соцсеть канала: из поля social, иначе определяем по ссылке.
  const SOCIAL_LABELS: Record<string, string> = {
    telegram: "Telegram",
    vk: "VK",
    max: "Max",
  };
  const seoSocial = computed(() => {
    const social = String(place.value?.social || "").toLowerCase();
    if (SOCIAL_LABELS[social]) return SOCIAL_LABELS[social];
    const url = String(place.value?.url || "").toLowerCase();
    if (url.includes("vk.com") || url.includes("vk.ru")) return SOCIAL_LABELS.vk;
    if (url.includes("max.ru") || url.includes("my.mail.ru")) return SOCIAL_LABELS.max;
    return SOCIAL_LABELS.telegram;
  });

  const seoTitle = computed(() =>
    place.value?.name
      ? `${place.value.name} | Заказать рекламу в ${seoSocial.value} на Telepost`
      : `Заказать рекламу в ${seoSocial.value} на Telepost`
  );

  const seoDescription = computed(() =>
    place.value?.name
      ? `Купить пост в ${seoSocial.value} канале ${place.value.name} - сервис рекламы Telepost. Цена размещения, статистика, ERR, подписчики и охваты. Реклама для бизнеса в несколько кликов.`
      : `Купить пост в ${seoSocial.value} канале - сервис рекламы Telepost. Цена размещения, статистика, ERR, подписчики и охваты. Реклама для бизнеса в несколько кликов.`
  );

  // Канонический адрес — по слагу канала: бэк принимает и другие написания,
  // а в индексе страница должна быть одна. Домен берём из запроса, потому что
  // в BASE_URL на проде записан внутренний хост, а не telepost.pro.
  const requestUrl = useRequestURL();
  const canonicalUrl = computed(
    () => `${requestUrl.origin}/ch/${place.value?.slug || id.value}`
  );

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogType: "website",
    ogUrl: canonicalUrl,
    twitterCard: "summary_large_image",
    twitterTitle: seoTitle,
    twitterDescription: seoDescription,
  });

  useHead(() => ({
    link: [{ rel: "canonical", href: canonicalUrl.value }],
  }));

  definePageMeta({
    layout: "data",
    middleware: "channel-404",
  });

  const canGoBack = ref(false);
  const goBack = () => {
    if (canGoBack.value) {
      router.back();
    } else {
      router.push("/catalog");
    }
  };

  // Если данных нет (клиентская навигация без payload), загружаем их
  const getPlace = async () => {
    isLoading.value = true;
    try {
      const result = await PlaceService.getPlace(undefined, id.value);
      place.value = result as FullPlace;
    } catch (error: any) {
      if (error.response?.status === 404 || error.response?.status === 400) {
        throw createError({
          statusCode: 404,
          statusMessage: "Канал не найден",
        });
      }
      throw error;
    }
    isLoading.value = false;
  };

  watch(id, () => {
    if (id.value) getPlace();
  });

  onMounted(() => {
    canGoBack.value = window.history.length > 1;
    if (!place.value) getPlace();
  });
</script>
